import { usePostHog } from '@/analytics/usePostHog';
import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localizePath } from "@/lib/localeRoutes";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useAppUrl } from "@/hooks/useAppUrl";
import { getFooterCtaHref } from "@/lib/ctaLinks";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { getResourceLinks } from "@/lib/resourcesNavigation";

const footerLinkClass = "inline-flex min-w-0 max-w-full min-h-11 items-center rounded-sm py-2 text-sm text-white/80 underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-brand-deep-blue";

export const Footer = () => {
  const { t, i18n } = useTranslation("common");
  const appUrl = useAppUrl();
  const analytics = usePostHog();
  const { choice, isOpen, open: openCookieSettings } = useCookieConsent();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Keep the legal row reachable while the non-modal consent bar is open.
  const reserveConsentSpace = mounted && (choice === null || isOpen);
  const getPath = (path: string) => localizePath(path, i18n.language);
  const resources = getResourceLinks(i18n.language);
  const explore = [
    ["/", t("navigation.home")],
    ["/doctor-visits", t("navigation.doctorVisits")],
    ["/how-it-works", t("navigation.howItWorks")],
    ["/why-docio", t("navigation.whyDocio")],
    ["/about", t("navigation.about")],
    ["/contact", t("navigation.contact")],
  ];
  const resourceItems = [
    [resources.patients, t("navigation.patientArticles")],
    [resources.doctors, t("navigation.doctorArticles")],
    [resources.insurance, t("navigation.insuranceArticles")],
    [resources.tools, t("navigation.freeTools")],
    [resources.pharmacy, t("navigation.pharmacyFinder")],
  ];
  const locations = [
    ["/locations/antalya", t("footer.antalyaLocationGuides")],
    ["/locations/istanbul", t("footer.istanbulLocationGuides")],
    ["/locations/mugla", t("footer.muglaLocationGuides")],
  ];
  const policies = [
    ["/legal", t("footer.legal")],
    ["/privacy-policy", t("footer.privacyPolicy")],
    ["/terms-of-service", t("footer.termsOfService")],
    ["/physician-terms", t("footer.physicianTerms")],
    ["/insurance-terms", t("footer.insuranceTerms")],
  ];

  return (
    <footer className={`bg-brand-deep-blue break-words px-6 pt-16 text-white md:px-10 md:pt-20 ${reserveConsentSpace ? "pb-80 sm:pb-48" : "pb-7"}`}>
      <div className="brand-container">
        <div className="grid grid-cols-2 [&>*]:min-w-0 gap-x-8 gap-y-10 lg:grid-cols-[1.3fr_1fr_1fr_1.1fr] lg:gap-12">
          <div className="col-span-2 max-w-sm lg:col-span-1">
            <Link to={getPath("/")} aria-label={`Docio: ${t("navigation.home")}`} className="inline-flex min-h-12 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <span className="flex h-12 w-12 items-center justify-center overflow-hidden">
                <img src="/assets/docio-mark-80.webp" srcSet="/assets/docio-mark-80.webp 1x, /assets/docio-mark-160.webp 2x" alt="" width={80} height={80} loading="lazy" decoding="async" className="h-20 w-20 max-w-none shrink-0 translate-y-1 object-contain" />
              </span>
              <span className="text-[1.75rem] font-bold tracking-[-0.05em]">Docio</span>
            </Link>
            <p className="mb-4 mt-5 max-w-xs text-sm leading-6 text-white/80">{t("footer.description")}</p>
            <a onClick={() => analytics?.capture("contact_link_clicked", { location: "footer" })} href={`mailto:${t("footer.email")}`} className={footerLinkClass}>
              <Mail className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" /><span className="min-w-0 [overflow-wrap:anywhere]">{t("footer.email")}</span>
            </a>
            <AppStoreBadge location="footer" className="mt-5 items-start" />
          </div>

          <nav aria-labelledby="footer-explore">
            <h2 id="footer-explore" className="mb-1 flex min-h-11 items-center text-sm font-semibold">{t("footer.quickLinks")}</h2>
            <ul>{explore.map(([path, label]) => <li key={path}><Link to={getPath(path)} className={footerLinkClass}><span className="min-w-0">{label}</span></Link></li>)}</ul>
          </nav>

          <nav data-footer-resources aria-labelledby="footer-resources">
            <h2 id="footer-resources" className="mb-1 text-sm font-semibold"><Link to={resources.hub} className="inline-flex min-w-0 max-w-full min-h-11 items-center rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><span className="min-w-0">{t("navigation.freeResources")}</span></Link></h2>
            <ul>{resourceItems.map(([href, label]) => <li key={href}><Link to={href} className={footerLinkClass}><span className="min-w-0">{label}</span></Link></li>)}</ul>
          </nav>

          <div className="col-span-2 grid [&>*]:min-w-0 gap-8 sm:grid-cols-2 lg:col-span-1 lg:block">
            <nav aria-labelledby="footer-locations">
              <h2 id="footer-locations" className="mb-1 flex min-h-11 items-center text-sm font-semibold">{t("footer.locationGuides")}</h2>
              <ul>{locations.map(([path, label]) => <li key={path}><Link to={getPath(path)} className={footerLinkClass}><span className="min-w-0">{label}</span></Link></li>)}</ul>
            </nav>
            <nav aria-labelledby="footer-professionals" className="lg:mt-7">
              <h2 id="footer-professionals" className="mb-1 flex min-h-11 items-center text-sm font-semibold">{t("footer.forProfessionals")}</h2>
              <ul>
                <li><a onClick={() => analytics?.capture("cta_clicked", { location: "footer", cta_type: "join_as_doctor" })} href={getFooterCtaHref("joinAsDoctor", appUrl)} target="_blank" rel="noopener noreferrer" className={footerLinkClass}><span className="min-w-0">{t("footer.joinAsDoctor")}</span></a></li>
                <li><a onClick={() => analytics?.capture("cta_clicked", { location: "footer", cta_type: "partner_with_us" })} href={getFooterCtaHref("partnerWithUs", appUrl)} className={footerLinkClass}><span className="min-w-0">{t("footer.partnerWithUs")}</span></a></li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="mt-12 border-t border-white/20 pt-6 md:mt-16">
          <nav aria-label={t("footer.legal")} className="flex flex-wrap gap-x-5 gap-y-1">
            {policies.map(([path, label]) => <Link key={path} to={getPath(path)} className={`${footerLinkClass} text-xs`}><span className="min-w-0">{label}</span></Link>)}
            <button type="button" onClick={openCookieSettings} className={`${footerLinkClass} text-left text-xs`}><span className="min-w-0">{t("footer.cookieSettings")}</span></button>
          </nav>
          <p className="mt-5 text-xs leading-6 text-white/70">{t("footer.copyright", { year: new Date().getFullYear() })}</p>
        </div>
      </div>
    </footer>
  );
};
